import { useNavigate } from "react-router-dom";
import { ReactComponent as Search } from "src/assets/svg/search.svg";
import usePagination from "src/hooks/usePagination";
import { JobInterview, InterviewStatus } from "src/types";
import { getInterviewMetrics, getInterviewsPaginated } from "src/network/interview";
import PaginatedDiv from "src/components/PaginatedDiv";
import { InterviewMetrics, InterviewPaginatedParams } from "src/network/interview/type";
import { useEffect, useState } from "react";
import LoaderPage from "src/components/Loader/LoaderPage";
import Tabs from "src/components/Tabs";
import Input from "src/components/Form/Input/Input";
import useDebounce from "src/hooks/useDebounce";
import Layout from "src/router/AuthenticatedRouter/Layout";
import InvitedTable from "src/components/Tables/InterviewTables/InvitedTable";
import InterviewedTable from "src/components/Tables/InterviewTables/InterviewedTable";
import ShortlistedTable from "src/components/Tables/InterviewTables/ShortlistedTable";
import Empty from "src/components/EmptyPagination/Empty";
import { emptyInterviewTab } from "src/components/Tables/InterviewTables/empty";
import { CountCard } from "src/components/CountCard/CountCard";

type tab = "Invited" | "Interviewed" | "Shortlisted" | "Rejected";
const tabs: tab[] = ["Invited", "Interviewed", "Shortlisted", "Rejected"];
const tabFilters: { [key in tab]: InterviewPaginatedParams } = {
  Invited: { status: "INVITED" },
  Interviewed: { status: "COMPLETED", sort: [["ai_score", "DESC NULLS LAST"]] },
  Shortlisted: { status: "PASSED" },
  Rejected: { status: "FAILED" }
};

const InterviewPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: "INVITED", keyword: "" });
  const debouncedSetFilters = useDebounce(setFilters);
  const status = filters.status as InterviewStatus;
  const { data, count, fetching, fetch, reset } = usePagination<JobInterview>({
    fetchPage: getInterviewsPaginated,
    page_size: 10,
    filters
  });
  const [metrics, setMetrics] = useState<InterviewMetrics>();

  const emptyTab = emptyInterviewTab(status, "", navigate);

  // Fetch metrics
  useEffect(() => {
    (async () => {
      const m = await getInterviewMetrics();
      setMetrics(m);
    })();
  }, []);

  return (
    <Layout title="Interviews" className={`flex flex-col  !overflow-hidden `}>
      <div className="flex gap-16 mb-6">
        <CountCard color="black" title="Invited" count={metrics?.INVITED} />
        <CountCard color="yellow" title="Interviewed" count={metrics?.COMPLETED} />
        <CountCard color="green" title="Shorlisted" count={metrics?.PASSED} />
        <CountCard color="red" title="Rejected" count={metrics?.FAILED} />
      </div>

      <div className="flex justify-between items-center mb-6">
        <Tabs
          style={0}
          className="mb-0"
          tabs={tabs}
          disabled={fetching}
          onChange={(s) => setFilters({ ...tabFilters[s] })}
        />

        <div className="flex justify-between">
          <Input
            wrapClassName=" !w-[370px]   !h-[56px] "
            className="!h-full  !rounded-full "
            onChange={(e) => debouncedSetFilters({ ...filters, keyword: e.target.value })}
            placeholder="Search by name or email..."
            icon={<Search className="w-6 h-6 " />}
          />
        </div>
      </div>

      {count === 0 && <Empty className="flex-1 overflow-scroll" {...emptyTab["active"]} />}

      {!data && <LoaderPage />}

      {data && !!count && (
        <PaginatedDiv
          className="!h-auto flex-1 "
          onLimit={fetch}
          loading={fetching}
          count={count}
          currentCount={data?.length}
        >
          {status === "INVITED" && <InvitedTable data={data} reset={reset} />}
          {status === "COMPLETED" && <InterviewedTable data={data} reset={reset} />}
          {status === "PASSED" && <ShortlistedTable data={data} reset={reset} />}
          {status === "FAILED" && <ShortlistedTable data={data} reset={reset} />}
        </PaginatedDiv>
      )}
    </Layout>
  );
};

export default InterviewPage;
